import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChefHat,
  Clock,
  Flame,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, StatusPill } from "@/components/ui";
import {
  getKdsBoard,
  KITCHEN_TICKET_LABEL,
  KITCHEN_TICKET_TONE,
  listKitchenAreas,
  updateKitchenTicketStatus,
  type KitchenTicketStatus,
} from "@/lib/restaurant";

export function KdsScreen() {
  const queryClient = useQueryClient();
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);

  // Cargar áreas de preparación
  const areasQuery = useQuery({
    queryKey: ["restaurant", "areas"],
    queryFn: listKitchenAreas,
  });

  const areas = areasQuery.data ?? [];
  const currentAreaId = selectedAreaId || areas[0]?.id;

  // Cargar tablero KDS de comandas activas
  const boardQuery = useQuery({
    queryKey: ["restaurant", "kds", currentAreaId],
    queryFn: () => (currentAreaId ? getKdsBoard(currentAreaId) : Promise.resolve([])),
    enabled: Boolean(currentAreaId),
    refetchInterval: 5_000, // refresco cada 5 segundos
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: KitchenTicketStatus }) =>
      updateKitchenTicketStatus(ticketId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "kds"] });
    },
  });

  const tickets = boardQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Selector de áreas de cocina */}
      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-b pb-4">
        <div className="flex items-center gap-2 overflow-x-auto">
          {areas.map((area) => (
            <button
              key={area.id}
              type="button"
              onClick={() => setSelectedAreaId(area.id)}
              className={`flex items-center gap-2 rounded-msr px-4 py-2 text-sm font-semibold transition-colors ${
                currentAreaId === area.id
                  ? "bg-electric text-on-electric shadow-sm"
                  : "border-line bg-snow text-carbon hover:bg-line/50 border"
              }`}
            >
              <ChefHat className="size-4" />
              <span>{area.name}</span>
            </button>
          ))}
        </div>

        <div className="text-muted flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="inline-block size-2 animate-pulse rounded-full bg-emerald-500" />
            En vivo (5s)
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["restaurant", "kds"] })}
            icon={<RefreshCw className="size-3.5" />}
          >
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tablero de comandas */}
      {boardQuery.isLoading ? (
        <div className="text-muted flex h-64 items-center justify-center">
          <RefreshCw className="mr-2 size-5 animate-spin" />
          Cargando comandas de cocina...
        </div>
      ) : tickets.length === 0 ? (
        <div className="border-line bg-snow flex flex-col items-center justify-center rounded-2xl border border-dashed py-20 text-center">
          <ChefHat className="text-muted/40 mb-3 size-12" />
          <h3 className="text-carbon text-base font-semibold">No hay pedidos pendientes en esta área</h3>
          <p className="text-muted mt-1 text-sm">
            Los platillos enviados por los meseros aparecerán aquí automáticamente.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tickets.map((ticket) => {
            const minutos = Math.floor(
              (Date.now() - new Date(ticket.sent_at).getTime()) / 60_000
            );
            const urgente = minutos >= 15;

            return (
              <Card
                key={ticket.id}
                className={`flex flex-col justify-between border-2 p-4 transition-all ${
                  urgente
                    ? "border-rose-500/80 bg-rose-500/5 shadow-lg"
                    : ticket.status === "preparing"
                    ? "border-electric/60 bg-electric/5"
                    : ticket.status === "ready"
                    ? "border-emerald-500/60 bg-emerald-500/5"
                    : "border-line"
                }`}
              >
                <div>
                  {/* Cabecera del ticket */}
                  <div className="border-line flex items-start justify-between border-b pb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-carbon font-mono text-xl font-black">
                          #{ticket.number}
                        </span>
                        {ticket.table_code && (
                          <span className="bg-surface text-carbon border-line rounded border px-2 py-0.5 text-xs font-bold">
                            Mesa {ticket.table_code}
                          </span>
                        )}
                      </div>
                      <p className="text-muted mt-1 text-xs">
                        {ticket.waiter_name ? `Mesero: ${ticket.waiter_name}` : "Comanda"}
                      </p>
                    </div>

                    <div className="text-right">
                      <StatusPill tone={KITCHEN_TICKET_TONE[ticket.status]}>
                        {KITCHEN_TICKET_LABEL[ticket.status]}
                      </StatusPill>
                      <div
                        className={`mt-1 flex items-center justify-end gap-1 text-xs font-bold ${
                          urgente ? "text-danger animate-pulse" : "text-muted"
                        }`}
                      >
                        <Clock className="size-3" />
                        <span>hace {minutos} min</span>
                      </div>
                    </div>
                  </div>

                  {/* Lista de platillos */}
                  <div className="divide-line divide-y py-3">
                    {ticket.lines.map((line) => (
                      <div key={line.id} className="py-2.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-electric-text text-lg font-black">
                            {line.quantity}x
                          </span>
                          <span className="text-carbon text-base font-bold">
                            {line.name}
                          </span>
                        </div>

                        {/* Modificadores */}
                        {line.modifiers && line.modifiers.length > 0 && (
                          <div className="mt-1 ml-6 flex flex-wrap gap-1">
                            {line.modifiers.map((mod, idx) => (
                              <span
                                key={idx}
                                className="bg-warning-soft text-warning rounded px-2 py-0.5 text-xs font-semibold"
                              >
                                + {mod}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Notas */}
                        {line.notes && (
                          <p className="text-danger mt-1 ml-6 text-xs font-semibold italic">
                            Nota: {line.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Acciones de estado */}
                <div className="border-line border-t pt-3">
                  {ticket.status === "pending" && (
                    <Button
                      className="w-full"
                      size="sm"
                      onClick={() =>
                        updateStatusMutation.mutate({
                          ticketId: ticket.id,
                          status: "preparing",
                        })
                      }
                      icon={<Flame className="size-4" />}
                    >
                      Empezar Preparación
                    </Button>
                  )}

                  {ticket.status === "preparing" && (
                    <Button
                      className="w-full bg-emerald-600 text-white hover:bg-emerald-500"
                      size="sm"
                      onClick={() =>
                        updateStatusMutation.mutate({
                          ticketId: ticket.id,
                          status: "ready",
                        })
                      }
                      icon={<CheckCircle2 className="size-4" />}
                    >
                      Listo en Barra
                    </Button>
                  )}

                  {ticket.status === "ready" && (
                    <Button
                      className="w-full"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        updateStatusMutation.mutate({
                          ticketId: ticket.id,
                          status: "served",
                        })
                      }
                    >
                      Marcar Servido
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
